import { useEffect, useState } from 'react';

// Extends the observer's root far below the viewport so everything under the fold
// counts as "intersecting". `!isIntersecting` then means strictly "above the viewport
// top", and every above/not-above transition crosses the threshold — including a
// single jump from below the fold straight past the top (back-nav scroll restoration,
// anchor links), which a plain `threshold: 0` observer would never report.
const BELOW_VIEWPORT_ROOT_MARGIN = '0px 0px 100000px 0px';

/**
 * True once `target` has scrolled fully above the top of the viewport; false while it
 * is in view or still below the fold, and before the first observation (so SSR and
 * first paint agree on false).
 */
const useHasScrolledPast = (target: Element | null) => {
  const [hasScrolledPast, setHasScrolledPast] = useState(false);

  useEffect(() => {
    if (!target) {
      setHasScrolledPast(false);
      return undefined;
    }

    // Entries queue while the main thread is busy, so a fast flick past the target
    // and back can deliver both crossings in one batch — the last one is current.
    const observer = new IntersectionObserver(
      (entries) =>
        setHasScrolledPast(!entries[entries.length - 1].isIntersecting),
      { root: null, rootMargin: BELOW_VIEWPORT_ROOT_MARGIN, threshold: 0 }
    );
    observer.observe(target);

    return () => observer.disconnect();
  }, [target]);

  return hasScrolledPast;
};

export default useHasScrolledPast;
